import { eventTypes } from "@/components/apprenticeship/candidate/utils";
import { BookEventSlot } from "@/components/ui/booking/book-event-slot";
import { RescheduleEventSlot } from "@/components/ui/booking/reschedule-event-slot";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useUser } from "@/providers/user";
import { ApprenticeshipRegistrationType } from "@/types/apprenticeship/apprentice";
import { ApprenticeshipAppointmentType } from "@/types/apprenticeship/enterprise";
import { CalendarCog, CalendarPlus } from "lucide-react";
import { DateTime } from "luxon";
import { useState, useEffect } from "react";
import { useFetcher } from "react-router-dom";
import { toast } from "sonner";

export function AppointmentDialog({
    registration,
    appointment,
    size = 20,
    children
}: {
    registration: ApprenticeshipRegistrationType;
    appointment?: ApprenticeshipAppointmentType;
    size?: number;
    children?: React.ReactNode;
}) {
    const { user } = useUser();
    const appointmentFetcher = useFetcher();
    const [isOpen, setIsOpen] = useState(false);
    const [eventType, setEventType] = useState<string>("");

    useEffect(() => {
        if (appointmentFetcher.data) {
            if (appointmentFetcher.data.success) {
                setIsOpen(false);
            } else {
                toast.error("Erreur lors de la création du rendez-vous");
            }
        }
    }, [appointmentFetcher.data]);

    const getBookingParticipants = (eventType: string) => {
        switch (eventType.toString()) {
            case "17":
                // Onboarding entreprise => invite le tuteur
                return [
                    {
                        email: registration.cerfa_tutor_email ?? "",
                        first_name: registration.cerfa_tutor_first_name ?? "",
                        last_name: registration.cerfa_tutor_last_name ?? "",
                        phone_number: registration.cerfa_tutor_mobile_phone ?? ""
                    }
                ];
            case "8":
                // Entretien tri-partite => invite le candidat et le tuteur
                return [
                    {
                        email: registration.cerfa_tutor_email ?? "",
                        first_name: registration.cerfa_tutor_first_name ?? "",
                        last_name: registration.cerfa_tutor_last_name ?? "",
                        phone_number: registration.cerfa_tutor_mobile_phone ?? ""
                    },
                    {
                        email: registration.email ?? "",
                        first_name: registration.first_name ?? "",
                        last_name: registration.last_name ?? "",
                        phone_number: registration.mobile_phone ?? ""
                    }
                ];
            default:
                // default participant is the candidate
                return [
                    {
                        email: registration.email ?? "",
                        first_name: registration.first_name ?? "",
                        last_name: registration.last_name ?? "",
                        phone_number: registration.mobile_phone ?? ""
                    }
                ];
        }
    };

    return (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger
                asChild
                onClick={() => {
                    setIsOpen(true);
                }}
            >
                {children || (appointment ? <CalendarCog width={size} height={size} cursor="pointer" /> : <CalendarPlus width={size} height={size} cursor="pointer" />)}
            </DialogTrigger>
            <DialogContent className="max-h-dvh w-full max-w-screen-xl overflow-auto">
                <DialogHeader>
                    <DialogTitle>{appointment ? `Replanifier un rendez-vous (${DateTime.fromISO(appointment.dt_start).toLocaleString(DateTime.DATETIME_MED)})` : "Ajouter un rendez-vous"}</DialogTitle>
                </DialogHeader>

                {!appointment ? (
                    <>
                        <Select onValueChange={value => setEventType(value)} value={eventType}>
                            <SelectTrigger className={eventType ? "" : "text-muted-foreground"}>
                                <SelectValue placeholder="Sélectionner un type d'évènement" />
                            </SelectTrigger>
                            <SelectContent>
                                {eventTypes
                                    .map(et => {
                                        return et.allowed_users.includes(user?.email ?? "") ? { ...et, display: true } : et;
                                    })
                                    .filter(et => et.display)
                                    .map(s => (
                                        <SelectItem value={s.id.toString()}>{s.name}</SelectItem>
                                    ))}
                            </SelectContent>
                        </Select>
                        {eventType && (
                            <BookEventSlot
                                registration={registration}
                                eventTypeId={eventType}
                                hostEmail={user?.email}
                                participants={getBookingParticipants(eventType.toString())}
                                onEventSlotBooked={slot => {
                                    appointmentFetcher.submit(
                                        {
                                            host: {
                                                email: slot.host.email
                                            },
                                            event_type: eventTypes.find(e => e.id === slot.event_type.id)?.code || "",
                                            dt_start: slot.start_time,
                                            dt_end: slot.end_time,
                                            external_id: `lms_booking:${slot.id}`
                                        },
                                        { method: "post", action: `/alternance/candidats/${registration.id}/rendez-vous`, encType: "application/json" }
                                    );
                                }}
                            />
                        )}
                    </>
                ) : (
                    <RescheduleEventSlot
                        eventSlotId={appointment.external_id.split(":")[1]}
                        eventTypeId={eventTypes.find(et => et.code == appointment.event_type)?.id.toString() || "0"}
                        hostEmail={appointment.host?.email || undefined}
                        onEventSlotRescheduled={slot => {
                            appointmentFetcher.submit(
                                {
                                    host: {
                                        email: slot.host.email
                                    },
                                    dt_start: slot.start_time,
                                    dt_end: slot.end_time
                                },
                                { method: "post", action: `/alternance/candidats/${registration.id}/rendez-vous/${appointment.id}`, encType: "application/json" }
                            );
                        }}
                    />
                )}
            </DialogContent>
        </Dialog>
    );
}
